import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { cx } from '../ui/cx';
import s from './screens.module.scss';

/**
 * L'entree.
 *
 * Un code vide cree un salon : c'est la regle du serveur, et elle donne les
 * deux boutons sans qu'on ait a inventer une route « creer ». Le code arrive
 * ensuite, dictable a voix haute — l'alphabet du serveur exclut deja les
 * caracteres qu'on confond (O/0, I/1).
 *
 * Le nom est memorise. Entre amis on relance trois parties de suite, et
 * retaper son prenom a chaque fois est le genre de friction qui fait qu'on
 * arrete de proposer le jeu.
 */
export interface HomeScreenProps {
  connecting: boolean;
  onJoin(room: string, name: string): void;
}

const NAME_KEY = 'guess:name';

export function HomeScreen({ connecting, onJoin }: HomeScreenProps) {
  const [name, setName] = useState('');
  const [room, setRoom] = useState('');

  useEffect(() => {
    try {
      setName(window.localStorage.getItem(NAME_KEY) ?? '');
    } catch {
      /* stockage refuse : on demandera le nom, c'est tout */
    }
    // Un lien d'invitation porte le code : `?r=ABCD`. On pre-remplit, on ne
    // rejoint pas tout seul — personne n'aime etre assis a une table sans
    // avoir donne son nom.
    const code = new URLSearchParams(window.location.search).get('r');
    if (code) setRoom(code.toUpperCase());
  }, []);

  const go = (target: string) => {
    const clean = name.trim();
    if (!clean) return;
    try {
      window.localStorage.setItem(NAME_KEY, clean);
    } catch {
      /* sans importance */
    }
    onJoin(target, clean);
  };

  return (
    <main className={s.home}>
      <header className={s.homeMasthead}>
        <h1 className={s.homeTitle}>
          <span className={s.homeTitleLine}>Complete</span>
          <span className={s.homeTitleLine}>la reference</span>
        </h1>
        <p className={s.homeTagline}>
          Un indice se devoile. La cote baisse. Repondre tot paie mieux, se tromper coute la mise.
        </p>
      </header>

      <form
        className={s.homeForm}
        onSubmit={(event) => {
          event.preventDefault();
          go(room);
        }}
      >
        <label className={s.homeField}>
          <span className={s.homeLabel}>Ton nom</span>
          <input
            className={s.homeInput}
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={16}
            placeholder="Ralph"
            autoComplete="nickname"
            required
          />
        </label>

        <label className={s.homeField}>
          <span className={s.homeLabel}>Code du salon</span>
          <input
            className={cx(s.homeInput, s.homeInputCode)}
            value={room}
            onChange={(event) => setRoom(event.target.value.toUpperCase())}
            maxLength={4}
            placeholder="ABCD"
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <div className={s.homeActions}>
          <Button
            type="submit"
            tone="accent"
            size="lg"
            disabled={connecting || name.trim() === '' || room.trim() === ''}
          >
            Rejoindre
          </Button>
          <Button
            tone="action"
            size="lg"
            disabled={connecting || name.trim() === ''}
            onClick={() => go('')}
          >
            Nouvelle partie
          </Button>
        </div>
      </form>

      {connecting ? <p className={s.homeStatus}>Connexion...</p> : null}
    </main>
  );
}
